'use client';

import { getIsTrustedCustomer } from '@/components/intercom/intercom-wrapper';
import { AnimatedIndicator } from '@/components/ui/animated-indicator';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useEnvironment } from '@/hooks/use-environment';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { FEATURE_FLAGS } from '@/hooks/use-feature-flag/constants';
import { usePerms } from '@/hooks/usePerms';
import { cn } from '@/lib/cn';
import { SUPPORT_LINK } from '@/lib/support';
import { Urls } from '@/lib/urls';
import { useSession } from '@/providers/session-provider';
import { useSignOut } from '@/providers/session-provider/hooks/use-sign-out';
import { BASE_BACKEND_URL } from '@/requests/constants';
import { useGetCount } from '@/requests/hooks';
import { HeadsetIcon, MessageCircleMoreIcon } from 'lucide-react';
import { IfPigelloUser } from '@/components/if-pigello-user';
import { SwitchAccountModal } from './components/switch-account-modal';
import {
  openHubSpotChat,
  subscribeToHubSpotUnreadCount,
} from '@/app/hubspot-chat';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, buttonVariants } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTrigger,
} from '../../../ui/sheet';
import { NotificationSheetContent } from './components/notifications/notification-sheet-content';
import { HIDDEN_NOTIFICATIONS_FILTER } from './components/notifications/utils';
import { ReleaseNotesSheetContent } from './components/release-notes/release-notes-sheet-content';
import { useReleaseNoteReadStatus } from './components/release-notes/use-has-read-latest';
import { Icons } from '@/components/icons';


export function FloatingMenu({ isSettings }: { isSettings?: boolean }) {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!window.Intercom) return;

    window.Intercom('onUnreadCountChange', (unread: number) =>
      setUnreadCount(unread)
    );
  }, []);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [switchAccountOpen, setSwitchAccountOpen] = useState(false);
  const {
    data: {
      user,
      session: { customerId },
    },
  } = useSession();
  const signOut = useSignOut();
  const { canView } = usePerms('notification');
  const { data: notifications } = useGetCount({
    modelName: 'notification',
    filters: {
      sent_to_organization_user: { __in: user?.id ?? '' },
      read: { noop: false },
      ...HIDDEN_NOTIFICATIONS_FILTER,
    },
    enabled: !!user?.id && canView,
  });

  const { isPigelloUser } = useEnvironment();
  const showDevLogout = isPigelloUser;

  const isHubSpotChatEnabled = useFeatureFlag(FEATURE_FLAGS.HUBSPOT_CHAT);
  const [hubSpotUnreadCount, setHubSpotUnreadCount] = useState(0);

  useEffect(() => {
    if (!isHubSpotChatEnabled) return;
    return subscribeToHubSpotUnreadCount(setHubSpotUnreadCount);
  }, [isHubSpotChatEnabled]);

  const isTrustedCustomer = getIsTrustedCustomer(customerId);
  const isDev = BASE_BACKEND_URL?.includes('dev.api');

  const { hasReadLatest: hasReadLatestReleaseNotes, markAsRead } =
    useReleaseNoteReadStatus();

  return (
    <div className='absolute bottom-0 z-50 h-auto w-full bg-linear-to-t from-background to-transparent px-4 pb-4'>
      <SwitchAccountModal
        open={switchAccountOpen}
        onOpenChange={setSwitchAccountOpen}
      />
      <div className='size-full overflow-hidden rounded-md bg-linear-to-r from-primary/30 via-border to-primary/30 p-px shadow-[10px_-24px_10px_-17px_rgba(0,0,0,0.06),0px_2px_4px_2px_rgba(81,101,251,0.08)]'>
        <div>
          <div
            className={cn(
              'flex w-full items-center justify-between rounded-md bg-surface px-4 py-2',
              {
                'rounded-b-none': !isDev || isTrustedCustomer,
              }
            )}
          >
            <DropdownMenu>
              <Tooltip>
                <DropdownMenuTrigger asChild>
                  <TooltipTrigger asChild>
                    {user?.profilePicture?.get ? (
                      <Image
                        src={user?.profilePicture?.get}
                        alt={user?.communicationName}
                        width={28}
                        height={28}
                        className='size-7 cursor-pointer rounded-full border border-primary bg-cover bg-center object-cover object-center'
                      />
                    ) : (
                      <div className='flex size-7 cursor-pointer items-center justify-center rounded-full border bg-primary bg-cover text-xs text-primary-foreground transition-colors hover:bg-primary/50'>
                        <span className='font-medium uppercase'>
                          {user?.firstName?.[0]}
                          {user?.lastName?.[0]}
                        </span>
                      </div>
                    )}
                  </TooltipTrigger>
                </DropdownMenuTrigger>
                <TooltipContent>Inställningar</TooltipContent>
              </Tooltip>
              <DropdownMenuContent align='start'>
                {!isSettings && (
                  <DropdownMenuItem
                    className={cn('flex w-full cursor-pointer items-center')}
                    asChild
                  >
                    <Link
                      prefetch={false}
                      className='flex items-center'
                      href={Urls.settings.profile.settings}
                    >
                      <Icons.settings width={20} className='mr-2' />
                      Inställningar
                    </Link>
                  </DropdownMenuItem>
                )}

                <IfPigelloUser>
                  <DropdownMenuItem
                    onClick={() => setSwitchAccountOpen(true)}
                    className='cursor-pointer'
                  >
                    <Icons.transfer width={20} className='mr-2' />
                    Byt konto{' '}
                    <span className='ml-1 text-sm text-muted-foreground'>
                      (dev)
                    </span>
                  </DropdownMenuItem>
                </IfPigelloUser>

                {showDevLogout && (
                  <DropdownMenuItem
                    onClick={() => signOut(false)}
                    className='cursor-pointer text-destructive'
                  >
                    <Icons.logout width={20} className='mr-2' />
                    Logga ut{' '}
                    <span className='ml-1 text-sm text-muted-foreground'>
                      (dev)
                    </span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => signOut()}
                  className='cursor-pointer text-destructive'
                >
                  <Icons.logout width={20} className='mr-2' />
                  Logga ut
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <Tooltip>
                <SheetTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button
                      className='relative'
                      variant={'outline'}
                      size={'icon'}
                      disabled={!canView}
                    >
                      {!!notifications && notifications > 0 && (
                        <AnimatedIndicator />
                      )}
                      <Icons.notification className='size-5 text-foreground' />
                    </Button>
                  </TooltipTrigger>
                </SheetTrigger>
                <TooltipContent>Notiser</TooltipContent>
              </Tooltip>
              <SheetContent className='flex flex-col gap-4 p-0'>
                <SheetDescription className='sr-only'>
                  Alla slags användarnotiser
                </SheetDescription>
                <NotificationSheetContent
                  userId={user.id}
                  setSheetOpen={setSheetOpen}
                />
              </SheetContent>
            </Sheet>
            <Sheet>
              <Tooltip>
                <SheetTrigger asChild>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={markAsRead}
                      variant={'outline'}
                      className='relative'
                      size={'icon'}
                    >
                      {!hasReadLatestReleaseNotes && <AnimatedIndicator />}
                      <Icons.releaseNotes className='size-5 text-foreground' />
                    </Button>
                  </TooltipTrigger>
                </SheetTrigger>
                <TooltipContent>Release notes</TooltipContent>
              </Tooltip>
              <SheetContent className='flex flex-col gap-4 p-0'>
                <SheetDescription className='sr-only'>
                  Release notes gällande hela applikationen
                </SheetDescription>
                <ReleaseNotesSheetContent />
              </SheetContent>
            </Sheet>

            <Tooltip>
              <TooltipTrigger>
                <a
                  className={cn(
                    buttonVariants({ variant: 'outline', size: 'icon' })
                  )}
                  href={SUPPORT_LINK}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <Icons.support className='size-5 text-foreground' />
                </a>
              </TooltipTrigger>
              <TooltipContent>Supportwebb</TooltipContent>
            </Tooltip>

            {isHubSpotChatEnabled && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className='relative'
                    variant={'outline'}
                    size={'icon'}
                    onClick={() =>
                      openHubSpotChat(() =>
                        toast.error('Chatten är inte tillgänglig just nu')
                      )
                    }
                  >
                    {hubSpotUnreadCount > 0 && <AnimatedIndicator />}
                    <MessageCircleMoreIcon className='size-5 text-foreground' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Chatta med oss</TooltipContent>
              </Tooltip>
            )}
          </div>

          {(!isDev || isTrustedCustomer) && (
            <button
              onClick={() =>
                window.Intercom
                  ? window.Intercom('show')
                  : alert('Chatt ej initialiserad än, försök igen')
              }
              className='group flex w-full items-center justify-between rounded-b-md border-t bg-surface px-4 py-1 text-sm text-secondary-foreground transition-all hover:bg-primary hover:text-primary-foreground'
            >
              <div className='text-xs font-medium'>Supportchatt </div>
              <div className='flex items-center gap-2'>
                {unreadCount > 0 && (
                  <Badge
                    className='animate-pulse px-1 text-xs leading-none'
                    variant='default'
                  >
                    {unreadCount}
                  </Badge>
                )}
                <HeadsetIcon className='size-4' />
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
